import { useCallback, useEffect, useState } from "react";
import { Checkbox } from "@fluentui/react/lib/Checkbox";
import { Stack, StackItem } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";

import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { WithTranslator } from "Components/Generic/Translator";
import { IItem } from "Model/Storage/IItem";
import { getAddress } from "Utils/Address";
import { hasNoElements } from "Utils/Array";
import { getName } from "Utils/Item";
import { useCompanySelectionStyles } from "./UseCompanySelectionStyles";
import { ICompaniesSelectionProps } from "./ICompaniesSelectionProps";

export const CompaniesSelection = WithTheme(
  WithTranslator((props: ICompaniesSelectionProps): JSX.Element => {
    const {
      companies,
      onMergeChange,
      translator,
      theme: { spacing }
    } = props;
    const [checkedStateList, setCheckedStateList] = useState([]);
    const styles = useCompanySelectionStyles();
    useEffect(() => {
      if (hasNoElements(companies)) {
        setCheckedStateList([]);
        return;
      }

      setCheckedStateList(Array.from({ length: companies.length }, () => false));
    }, [companies]);

    useEffect(() => {
      const companiesToMerge = checkedStateList.reduce((out, state, index) => {
        if (state === false) {
          return out;
        }

        return [...out, companies[index]];
      }, []);
      onMergeChange(`target.companies`, companiesToMerge);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkedStateList]);

    const generateOnChange = useCallback(
      (index) => () => {
        const updatedCheckedState = checkedStateList.map((item, i) => {
          if (i === index) {
            return !item;
          }
          return item;
        });

        setCheckedStateList(updatedCheckedState);
      },
      [checkedStateList]
    );

    if (hasNoElements(companies)) {
      return null;
    }

    return (
      <Stack className={styles.root} tokens={{ childrenGap: spacing.m }}>
        <Stack.Item>
          <Text variant="mediumPlus">{translator.get(`staging.merge.newcompanies`, `New companies`)}</Text>
        </Stack.Item>
        {companies.map((company, index) => (
          <StackItem key={company[`@id`]}>
            <Checkbox
              className={styles.checkbox}
              checked={checkedStateList[index]}
              onChange={generateOnChange(index)}
              onRenderLabel={generateOnRenderLabel(company, styles)}
            />
          </StackItem>
        ))}
      </Stack>
    );
  })
);

// TODO: Remove any type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function generateOnRenderLabel(company: IItem, styles: any) {
  return () => (
    <Stack className={styles.checkboxContent}>
      <StackItem className={styles.checkboxContentItem}>
        <Text block nowrap variant="mediumPlus">
          {getName(company)}
        </Text>
      </StackItem>
      <StackItem className={styles.checkboxContentItem}>
        <Text block nowrap>
          {getAddress(company)}
        </Text>
      </StackItem>
    </Stack>
  );
}
